import { Image, LineProgress, Row, Typography } from '@/components'
import { ComponentColors, ThemeColor } from '@/enums'
import { FormProvider } from '@/providers'
import { Button } from '@surein/ui'
import { ArrowRight, ChevronLeft } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { InferType } from 'yup'
import {
  FooterContainer,
  FormWrapper,
  HeaderContainer,
  ProgressBarContainer,
  ScrollableContent,
  StepContainer,
  LoaderImageContainer,
} from '../../styles'
import { useChecklistStepper } from './hooks/useChecklistStepper'

interface HeaderProps {
  title: string
  subtitle?: string
}

const Header = ({ title, subtitle }: HeaderProps) => {
  return (
    <HeaderContainer>
      <Typography bold variant="h4">
        {title}
      </Typography>
      {subtitle && (
        <Typography color={ThemeColor.glass500} variant="description">
          {subtitle}
        </Typography>
      )}
    </HeaderContainer>
  )
}

interface FooterProps {
  onSkip?: () => void
  onBack?: () => void
  hideBack?: boolean
  isLoading?: boolean
}

const Footer = ({
  onSkip,
  onBack,
  hideBack = false,
  isLoading = false,
}: FooterProps) => {
  const { t } = useTranslation()

  return (
    <FooterContainer
      justifyContent={!hideBack && onBack ? 'space-between' : 'flex-end'}
    >
      {!hideBack && onBack && (
        <Button
          variant="muted"
          shape="square"
          icon={ChevronLeft}
          onClick={onBack}
          type="button"
          loading={isLoading}
        />
      )}
      <Row>
        {!isLoading && onSkip && (
          <Button type="button" variant="link" onClick={onSkip}>
            <Typography variant="p2Body" color={ThemeColor.b50}>
              {t('common.skip')}
            </Typography>
          </Button>
        )}
        <Button
          type="submit"
          variant="primary"
          shape="square"
          suffixIcon={ArrowRight}
          loading={isLoading}
        >
          {t('common.next')}
        </Button>
      </Row>
    </FooterContainer>
  )
}

interface StepLayoutProps<T> {
  children: React.ReactNode
  title: string
  subtitle?: string
  onNext: (values: T) => void
  onBack?: () => void
  onSkip?: () => void
  validationSchema?: () => Yup.ObjectSchema<InferType<Yup.AnyObjectSchema>>
  isLoading?: boolean
  hideBack?: boolean
  initialValues?: T
}

export const StepLayout = <T,>({
  children,
  validationSchema,
  onNext,
  onSkip,
  title,
  subtitle,
  isLoading,
  hideBack,
  initialValues,
}: StepLayoutProps<T>) => {
  const {
    isFirstStep,
    goToPrevious,
    currentStep,
    maxSteps,
  } = useChecklistStepper()
  const percentage = (currentStep / maxSteps) * 100
  const displayPercentage = percentage <= 25 ? 25 : percentage

  return (
    <FormWrapper>
      <FormProvider
        initialValues={{ ...(initialValues || {}) }}
        validationSchema={validationSchema}
        onSubmit={(values) => onNext({ ...values })}
      >
        <ProgressBarContainer>
          <LineProgress
            color={ComponentColors.primary}
            percentage={displayPercentage}
            height="8px"
            width="200px"
          />
        </ProgressBarContainer>
        <Header title={title} subtitle={subtitle} />
        <ScrollableContent>{children}</ScrollableContent>
        <Footer
          onSkip={onSkip}
          hideBack={hideBack || isFirstStep}
          onBack={goToPrevious}
          isLoading={isLoading}
        />
      </FormProvider>
    </FormWrapper>
  )
}

export const LoadingSkeleton = () => {
  return (
    <StepContainer justifyContent="center" alignItems="center">
      <LoaderImageContainer>
        <Image name="logo" type="gif" width="100px" height="100px" />
      </LoaderImageContainer>
    </StepContainer>
  )
}
